import { PlainMessage } from '@bufbuild/protobuf';
import {
  GetBillOfMaterialsResponse,
  GetBillOfMaterialsResponse_Alert,
  GetBillOfMaterialsResponse_Alert_AlertType,
  GetBillOfMaterialsResponse_ReservoirItems,
} from '@wavingroup/aqora-v2-api/wavin/aqora/v2/aqora_service_pb';
import { TFunction } from 'i18next';
import {
  ReservoirModel,
  SystemModel,
} from '~/shared/models/system/SystemModel';
import { assertUnreachable } from '~/types/assert-type';

type PlainReservoirItem = Omit<
  PlainMessage<GetBillOfMaterialsResponse_ReservoirItems>,
  'subtotalGrossPrice'
>;

export type BillOfMaterialsItem = PlainReservoirItem & {
  subtotalGrossPrice: string;
  getTitle: (t: TFunction) => string;
  getAlerts: (t: TFunction) => string[];
};

export class BillOfMaterialsModel {
  totalGrossPrice: string;

  totalM2Price: string;

  items: BillOfMaterialsItem[];

  system: SystemModel;

  constructor(
    billOfMaterialsResponse: GetBillOfMaterialsResponse,
    system: SystemModel,
  ) {
    this.system = system;
    this.totalGrossPrice = Number(
      billOfMaterialsResponse.totalGrossPrice,
    ).toFixed(2);

    this.totalM2Price = Number(billOfMaterialsResponse.totalM2Price).toFixed(2);

    this.items = billOfMaterialsResponse.items.map(
      ({ subtotalGrossPrice, ...item }) => ({
        ...item,
        subtotalGrossPrice: Number(subtotalGrossPrice).toFixed(2),
        getTitle: this.getItemTitle(item),
        getAlerts: BillOfMaterialsModel.getItemAlerts(item),
      }),
    );
  }

  private getItemTitle(item: PlainReservoirItem) {
    return (t: TFunction) => {
      let title = t('billOfMaterials.table.titleAccessories');

      if (item.reservoirName) {
        const reservoirModel = this.system.reservoirById(
          ReservoirModel.extractReservoirId(item.reservoirName),
        );
        title = t('billOfMaterials.table.titleReservoirItems', {
          reservoirTitle: reservoirModel?.title,
        });
      }

      return title;
    };
  }

  private static getItemAlerts(item: PlainReservoirItem) {
    return (t: TFunction) =>
      item.alerts.map((alert) => BillOfMaterialsModel.mapAlert(t, alert));
  }

  private static mapAlert(
    t: TFunction,
    alert: PlainMessage<GetBillOfMaterialsResponse_Alert>,
  ) {
    switch (alert.alertType) {
      case GetBillOfMaterialsResponse_Alert_AlertType.INVALID_DRAIN_CONFIG:
        if (alert.error?.type.case !== 'configurationError') {
          throw new Error('Wrong error type');
        }
        return t('billOfMaterials.alerts.invalidDrainConfig', {
          drainName: alert.error?.type.value.title,
        });

      case GetBillOfMaterialsResponse_Alert_AlertType.TOO_LOW_AMOUNT_OF_DRAINS:
        if (alert.error?.type.case !== 'tooLow') {
          throw new Error('Wrong error type');
        }
        return t('billOfMaterials.alerts.tooLowAmountOfDrains', {
          count: alert.error?.type.value.min,
        });

      case GetBillOfMaterialsResponse_Alert_AlertType.CRATE_TYPE_NOT_SET:
        if (alert.error?.type.case !== 'configurationError') {
          throw new Error('Wrong error type');
        }
        return t('billOfMaterials.alerts.crateTypeNotSet', {
          reservoirName: alert.error?.type.value.title,
        });

      case GetBillOfMaterialsResponse_Alert_AlertType.NO_DIRECT_DRAIN:
        if (alert.error?.type.case !== 'configurationError') {
          throw new Error('Wrong error type');
        }
        return t('billOfMaterials.alerts.noDirectDrain', {
          reservoirName: alert.error?.type.value.title,
        });

      case GetBillOfMaterialsResponse_Alert_AlertType.POLDERVALVE_INVALID_DRAIN_TYPE:
        if (alert.error?.type.case !== 'configurationError') {
          throw new Error('Wrong error type');
        }
        return t('billOfMaterials.alerts.polderValveInvalidDrainType', {
          polderValve: alert.error?.type.value.title,
        });

      case GetBillOfMaterialsResponse_Alert_AlertType.POLDERVALVE_COUNT_LOW:
        if (alert.error?.type.case !== 'configurationError') {
          throw new Error('Wrong error type');
        }
        return t('billOfMaterials.alerts.polderValveCountLow', {
          reservoirName: alert.error?.type.value.title,
        });

      case GetBillOfMaterialsResponse_Alert_AlertType.UNSPECIFIED:
        throw new Error('Unspecified alert type');
      default:
        return assertUnreachable(alert.alertType);
    }
  }
}
